import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { OrganisationPaymentAccountingBox } from "../../features/organisations/boxes/OrganisationPaymentAccountingBox.js";
import { OrganisationPaymentInfoBox } from "../../features/organisations/boxes/OrganisationPaymentInfoBox.js";
import { useOrganisationPaymentQuery } from "./OrganisationPayment.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationPayment = ({ submenuItems }: Props) => {
  const { t } = useTranslate("OrganisationPageTopbar");

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Organisation", { ns: "OrganisationPageTopbar" }),
      to: "/organisation",
    },
    {
      title: t("Payment & Accounting", { ns: "OrganisationPageTopbar" }),
      to: "/organisation/profile",
    },
  ];

  const client = useApolloClient();
  const query = useOrganisationPaymentQuery({
    client,
  });

  const organisation = getDataOrNull(query.data?.viewer)?.organisation;
  const organisationSettings = getDataOrNull(query.data?.organisationSettings);
  const organisationCounters = getDataOrNull(query.data?.organisationCounters);

  return (
    <Page
      title={t("Payment & Accounting", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      breadcrumbsSize="medium"
    >
      {organisation && (
        <PageContainer>
          <Stack spacing={2} sx={{ width: "100%", maxWidth: 640 }}>
            <OrganisationPaymentInfoBox organisation={organisation} />

            <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
              {organisationSettings && organisationCounters && (
                <OrganisationPaymentAccountingBox
                  organisationId={organisation.id}
                  organisationCounters={organisationCounters}
                  organisationSettings={organisationSettings}
                />
              )}
            </RestrictedByOrganisationPermissionWithDebug>
          </Stack>
        </PageContainer>
      )}
    </Page>
  );
};
